import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Monitor, Clipboard, Upload, X, Check, RotateCcw, Loader2, Crop } from 'lucide-react';

interface CameraScreenshotCaptureProps {
  currentScreenshot?: string;
  onScreenshotCapture: (screenshotPath: string) => void;
  onScreenshotRemove: () => void;
}

const CameraScreenshotCapture: React.FC<CameraScreenshotCaptureProps> = ({
  currentScreenshot,
  onScreenshotCapture,
  onScreenshotRemove
}) => {
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCropping, setIsCropping] = useState(false);
  const [cropStart, setCropStart] = useState<{ x: number; y: number } | null>(null);
  const [cropArea, setCropArea] = useState<{ x: number; y: number; width: number; height: number } | null>(null);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  // Cleanup effect
  useEffect(() => {
    return () => {
      if (capturedImage && capturedImage.startsWith('blob:')) {
        URL.revokeObjectURL(capturedImage);
      }
    };
  }, [capturedImage]);

  // Screen Capture API
  const handleScreenCapture = async () => {
    try {
      setError(null);

      // Check if Screen Capture API is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
        setError('Tarayıcınız ekran yakalama özelliğini desteklemiyor');
        return;
      }

      const mediaStream = await navigator.mediaDevices.getDisplayMedia({
        video: { cursor: 'always' } as MediaTrackConstraints,
        audio: false
      });

      const track = mediaStream.getVideoTracks()[0];

      // Create video element to capture frame
      const video = document.createElement('video');
      video.srcObject = mediaStream;
      video.autoplay = true;

      await new Promise<void>((resolve) => {
        video.onloadedmetadata = () => {
          video.play();
          resolve();
        };
      });

      // Wait a bit for video to be ready
      await new Promise(resolve => setTimeout(resolve, 100));

      // Draw to canvas
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx?.drawImage(video, 0, 0);

      // Stop the stream
      track.stop();

      const dataUrl = canvas.toDataURL('image/png');
      setCapturedImage(dataUrl);
      setIsCropping(true);
      setCropArea(null);

    } catch (err: any) {
      if (err.name !== 'AbortError') {
        console.error('Screen capture error:', err);
        setError('Ekran yakalama iptal edildi veya bir hata oluştu');
      }
    }
  };

  // Clipboard Paste Handler
  const handlePaste = useCallback((e: ClipboardEvent) => {
    const items = Array.from(e.clipboardData?.items || []);
    const imageItem = items.find(item => item.type.startsWith('image/'));

    if (imageItem) {
      e.preventDefault();
      const file = imageItem.getAsFile();
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          setCapturedImage(event.target?.result as string);
          setIsCropping(true);
          setCropArea(null);
          setError(null);
        };
        reader.readAsDataURL(file);
      }
    }
  }, []);

  // Setup paste listener
  const setupPasteListener = () => {
    setError(null);
    const handler = (e: ClipboardEvent) => {
      handlePaste(e);
      document.removeEventListener('paste', handler);
    };
    document.addEventListener('paste', handler);

    // Show instruction
    setError('Şimdi Ctrl+V tuşlayın veya sağ tık yapıştır seçeneğini kullanın');

    // Remove listener after 10 seconds
    setTimeout(() => {
      document.removeEventListener('paste', handler);
    }, 10000);
  };

  // File Upload Handler
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setCapturedImage(event.target?.result as string);
        setIsCropping(true);
        setCropArea(null);
        setError(null);
      };
      reader.readAsDataURL(file);
    }
    // Reset input
    e.target.value = '';
  };

  // Crop handlers
  const handleCropMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isCropping || !imageContainerRef.current) return;

    const rect = imageContainerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setCropStart({ x, y });
    setCropArea({ x, y, width: 0, height: 0 });
  };

  const handleCropMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!cropStart || !imageContainerRef.current) return;

    const rect = imageContainerRef.current.getBoundingClientRect();
    const currentX = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const currentY = Math.max(0, Math.min(e.clientY - rect.top, rect.height));

    const x = Math.min(cropStart.x, currentX);
    const y = Math.min(cropStart.y, currentY);
    const width = Math.abs(currentX - cropStart.x);
    const height = Math.abs(currentY - cropStart.y);

    setCropArea({ x, y, width, height });
  };

  const handleCropMouseUp = () => {
    setCropStart(null);
  };

  // Apply crop and upload
  const handleApplyCrop = async () => {
    if (!capturedImage) return;

    setIsUploading(true);
    setError(null);

    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('Görüntü yüklenemedi'));
        img.src = capturedImage;
      });

      const canvas = canvasRef.current;
      if (!canvas) throw new Error('Canvas bulunamadı');

      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas context bulunamadı');

      // Calculate actual crop dimensions
      let cropX = 0, cropY = 0, cropWidth = img.width, cropHeight = img.height;

      if (cropArea && cropArea.width > 10 && cropArea.height > 10 && imageRef.current) {
        const displayedWidth = imageRef.current.clientWidth;
        const displayedHeight = imageRef.current.clientHeight;
        const scaleX = img.width / displayedWidth;
        const scaleY = img.height / displayedHeight;

        cropX = Math.round(cropArea.x * scaleX);
        cropY = Math.round(cropArea.y * scaleY);
        cropWidth = Math.round(cropArea.width * scaleX);
        cropHeight = Math.round(cropArea.height * scaleY);
      }

      canvas.width = cropWidth;
      canvas.height = cropHeight;
      ctx.drawImage(img, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);

      // Convert to blob
      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, 'image/png', 0.9);
      });

      if (!blob) {
        throw new Error('Görüntü işlenemedi');
      }

      // Upload
      const formData = new FormData();
      formData.append('screenshot', blob, 'screenshot.png');

      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/upload/camera-screenshot', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Screenshot yüklenemedi');
      }

      const result = await response.json();
      onScreenshotCapture(result.filename);
      handleReset();

    } catch (err: any) {
      console.error('Screenshot upload error:', err);
      setError(err.message || 'Screenshot yüklenirken hata oluştu');
    } finally {
      setIsUploading(false);
    }
  };

  const handleReset = () => {
    setCapturedImage(null);
    setIsCropping(false);
    setCropArea(null);
    setCropStart(null);
    setError(null);
  };

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        Kamera Görüntüsü
      </label>

      {/* Current Screenshot Preview */}
      {currentScreenshot && !capturedImage && (
        <div className="relative group">
          <img
            src={`/uploads/${currentScreenshot}`}
            alt="Kamera görüntüsü"
            className="w-full h-40 object-cover rounded-lg border border-gray-300 dark:border-gray-600"
            onError={(e) => {
              (e.target as HTMLImageElement).src = '/placeholder-camera.png';
            }}
          />
          <button
            type="button"
            onClick={onScreenshotRemove}
            className="absolute top-2 right-2 p-1.5 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-700"
            title="Görüntüyü kaldır"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Capture/Crop Mode */}
      {capturedImage && (
        <div className="space-y-3">
          <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
            <Crop className="w-3 h-3" />
            Kırpmak için görüntü üzerinde bir alan seçin (opsiyonel)
          </div>
          <div
            ref={imageContainerRef}
            className="relative border-2 border-dashed border-primary dark:border-primary rounded-lg overflow-hidden cursor-crosshair bg-gray-100 dark:bg-gray-800"
            onMouseDown={handleCropMouseDown}
            onMouseMove={handleCropMouseMove}
            onMouseUp={handleCropMouseUp}
            onMouseLeave={handleCropMouseUp}
          >
            <img
              ref={imageRef}
              src={capturedImage}
              alt="Yakalanan görüntü"
              className="max-w-full max-h-64 object-contain mx-auto select-none pointer-events-none"
              draggable={false}
            />
            {/* Crop overlay */}
            {cropArea && cropArea.width > 0 && cropArea.height > 0 && (
              <>
                {/* Dark overlay outside selection */}
                <div className="absolute inset-0 bg-black/40 pointer-events-none"
                     style={{
                       clipPath: `polygon(0% 0%, 0% 100%, ${cropArea.x}px 100%, ${cropArea.x}px ${cropArea.y}px, ${cropArea.x + cropArea.width}px ${cropArea.y}px, ${cropArea.x + cropArea.width}px ${cropArea.y + cropArea.height}px, ${cropArea.x}px ${cropArea.y + cropArea.height}px, ${cropArea.x}px 100%, 100% 100%, 100% 0%)`
                     }}
                />
                {/* Selection border */}
                <div
                  className="absolute border-2 border-primary bg-transparent pointer-events-none"
                  style={{
                    left: cropArea.x,
                    top: cropArea.y,
                    width: cropArea.width,
                    height: cropArea.height
                  }}
                />
              </>
            )}
          </div>

          <div className="flex justify-center space-x-2">
            <button
              type="button"
              onClick={handleReset}
              disabled={isUploading}
              className="flex items-center px-3 py-2 text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              Yeniden
            </button>
            <button
              type="button"
              onClick={handleApplyCrop}
              disabled={isUploading}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Yükleniyor...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-1" />
                  Kaydet
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Capture Buttons */}
      {!capturedImage && !currentScreenshot && (
        <div className="grid grid-cols-3 gap-2">
          <button
            type="button"
            onClick={handleScreenCapture}
            className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-primary hover:bg-primary/10 dark:hover:bg-primary/20 transition-colors"
          >
            <Monitor className="w-6 h-6 text-primary mb-1" />
            <span className="text-xs text-gray-600 dark:text-gray-400">Ekran Seç</span>
          </button>

          <button
            type="button"
            onClick={setupPasteListener}
            className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-green-500 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
          >
            <Clipboard className="w-6 h-6 text-green-500 mb-1" />
            <span className="text-xs text-gray-600 dark:text-gray-400">Yapıştır</span>
          </button>

          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors"
          >
            <Upload className="w-6 h-6 text-purple-500 mb-1" />
            <span className="text-xs text-gray-600 dark:text-gray-400">Dosya Seç</span>
          </button>
        </div>
      )}

      {/* Add new screenshot button when there's already one */}
      {currentScreenshot && !capturedImage && (
        <div className="grid grid-cols-3 gap-2 mt-2">
          <button
            type="button"
            onClick={handleScreenCapture}
            className="flex items-center justify-center p-2 text-xs text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <Monitor className="w-4 h-4 mr-1" />
            Ekran
          </button>
          <button
            type="button"
            onClick={setupPasteListener}
            className="flex items-center justify-center p-2 text-xs text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <Clipboard className="w-4 h-4 mr-1" />
            Yapıştır
          </button>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center justify-center p-2 text-xs text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <Upload className="w-4 h-4 mr-1" />
            Dosya
          </button>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileSelect}
      />

      <canvas ref={canvasRef} className="hidden" />

      {error && (
        <p className={`text-sm ${error.includes('Ctrl+V') ? 'text-primary dark:text-primary' : 'text-red-600 dark:text-red-400'}`}>
          {error}
        </p>
      )}
    </div>
  );
};

export default CameraScreenshotCapture;
